import rawExpectedParentOrigins from "../brand-config/generated.allowed-parent-origins.json" with { type: "json" };
import type {
	IframeDataMessage,
	JwtBody,
	JwtKey,
	JwtResult,
	PostMessageData,
	PrefixedJwtKey,
	TransformedJwtBody,
} from "./types";
import { checkIsAuthenticated, checkIsDeploymentActive, getDeploymentId, getEnvironment } from "./utils.js";
import { createRemoteJWKSet, decodeJwt, jwtVerify } from "jose";

const disconnectReasonElem = document.querySelector(".disconnection-reason") as HTMLDivElement;
const messageIframe = document.getElementById("messageWidget") as HTMLIFrameElement;
const messageWindow = messageIframe.contentWindow;

const closeButton = createCloseWindowButton();

const LOADING_TIMEOUT_MS = 45_000; // 45 seconds
let loadingTimeoutId: ReturnType<typeof setTimeout> | null = null;

// After a trash-icon clear we call window.close(). A genuine close tears down the page before this
// delay elapses; if the page is still alive afterwards the close was a no-op (URL opened directly in
// a tab, not via a script), so we show the end-of-chat fallback panel instead.
const WINDOW_CLOSE_FALLBACK_DELAY_MS = 250;
let isLoadingComplete = false;

// 7.5 seconds to receive first bot message after Messenger.ready before reloading the iframe.
// This prevents users from being stuck behind an infinite loading screen in Genesys chat if the bot fails to load properly
const BOT_TIMEOUT_MS = 7_500;
const botTimeoutSeconds = BOT_TIMEOUT_MS / 1000;
let botTimeoutId: ReturnType<typeof setTimeout> | null = null;
let botTimerWasPaused = false; // true while auth popup is open so we restart fresh after login

/**
 * How many consecutive bot-timeout iframe reloads we'll attempt before giving up
 * and showing the load-error UI. Without a cap, a Genesys SDK plugin-load failure
 * (e.g. messagingservice.min.js failing to fetch) traps the user in an infinite
 * 7.5s reload loop because MessagingService.started never fires, MESSENGER_END_LOAD
 * is never posted, and the spinner never hides. With the cap, after 2 reloads the
 * user sees the disconnect UI with a "Reload the chat" button instead.
 *
 * Reset to 0 on MESSAGE_ACTIVITY — once any real chat activity proves the session
 * is alive, a future bot-timeout starts with a fresh retry budget.
 */
const MAX_BOT_TIMEOUT_RELOADS = 2;
let botTimeoutReloadCount = 0;

const isAuthenticatedSession = checkIsAuthenticated();
/**
 * Before a human agent enters the conversation, the questionnaire is disconnected
 * after this many minutes without customer content. Bot messages never extend it.
 */
const QUESTIONNAIRE_INACTIVITY_TIMEOUT_MINUTES = 20;

/**
 * Grace period (in minutes) after Genesys reports a normal `CONVERSATION_ENDED` before the wrapper
 * disconnects the chat UI. Any customer message during this period cancels the pending disconnect,
 * allowing the conversation to continue.
 */
const CONVERSATION_END_TIMEOUT_MINUTES = 20;

const URL_REGEX = /https?:\/\/[^\s]+/g;

const USER_UNRESPONSIVE_MESSAGE = `
It appears our chat was interrupted. If you still need assistance or need anything further, please do not hesitate to get in touch with us on 1300 10 1234 and we'd be glad to help.
As a friendly reminder, your policy can also be managed via our 'My Insurance Portal' available here: https://insurance.everyday.com.au/login.html
Through this, you can:
-Manage Payment
-Update Contact Information
-Download your latest Certificate of Insurance`;

const END_OF_SESSION_MESSAGE = [
	"This interaction has now been closed.",
	"Please don't hesitate to chat with us again if you require any assistance in the future.",
].join(" ");

const CHAT_UNAVAILABLE_MESSAGE = [
	"Unfortunately we are experiencing technical difficulties and we are unable to initiate a chat at this time.",
	"Please try again later or try giving us a call on 1300 101 234 Monday to Friday, 8am-8pm.",
	"(AEST/AEDT), Saturday and Sunday, 9am-5pm (AEST/AEDT)",
].join(" ");

let ownershipSessionId: string | null = null;
let hasDisconnected = false;
let authPopupWindow: Window | null = null;
let conversationEndedTimeoutId: ReturnType<typeof setTimeout> | null = null;

const jwtToken = new URLSearchParams(location.search).get("token") ?? "";

function getValidatedJwtData(): JwtResult {
	if (!jwtToken) {
		return { isValid: false, error: "No token was provided in the query parameters" };
	}

	const parts = jwtToken.split(".");
	const isIn3Parts = parts.length === 3;
	if (!isIn3Parts) {
		return { isValid: false, error: "Malformed token: expected 3 parts" };
	}

	let jwtData: JwtBody;
	try {
		jwtData = decodeJwt<JwtBody>(jwtToken);
	} catch {
		return { isValid: false, error: "Failed to decode the token" };
	}

	const { mandyId, enquiryType, productType, exp, iat } = jwtData;

	const nowSeconds = Math.floor(Date.now() / 1000);
	const isExpired = exp < nowSeconds;
	if (isExpired) {
		return { isValid: false, error: "Token has expired" };
	}
	const isInFuture = iat > nowSeconds;
	if (isInFuture) {
		return { isValid: false, error: "Token is not valid yet" };
	}

	function missingPropertyError(propName: keyof JwtBody): JwtResult {
		return { isValid: false, error: `Token is missing the '${propName}' property` };
	}

	if (!mandyId) {
		return missingPropertyError("mandyId");
	}
	if (!enquiryType) {
		return missingPropertyError("enquiryType");
	}
	if (!productType) {
		return missingPropertyError("productType");
	}

	const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
	const isManyIdValid = uuidRegex.test(mandyId);
	if (!isManyIdValid) {
		return { isValid: false, error: "Mandy Id is not a valid UUID" };
	}

	return { isValid: true, data: transformJwtBody(jwtData) };
}

function transformJwtBody(rawJwtBody: JwtBody): TransformedJwtBody {
	const entries = Object.entries(rawJwtBody) as Array<[JwtKey, JwtBody[JwtKey]]>;

	return Object.fromEntries(
		// Genesys customAttributes are string key/value pairs, so stringify every claim value —
		// otherwise the numeric iat/exp timestamps may be silently dropped by the Database plugin.
		entries.map(([claimName, claimValue]): [PrefixedJwtKey, string] => [`mandy:${claimName}`, String(claimValue)]),
	) as TransformedJwtBody;
}

const jwtResult = getValidatedJwtData();
console.log("Decoded JWT result:", jwtResult);

verifyJwt(jwtResult)
	.then(() => initialiseChatWidget())
	.catch((err) => {
		const error = err as Error;
		showLoadError(`Failed to verify the JWT. ${error.message}`);
	});

async function verifyJwt(jwtResult: JwtResult) {
	if (!jwtResult.isValid) {
		console.error("JWT is invalid:", jwtResult.error);
		throw new Error(`Token is invalid: ${jwtResult.error}`);
	}
	try {
		const issuer = jwtResult.data?.["mandy:iss"];
		const oidcConfiguration = await fetch(`${issuer}/.well-known/openid-configuration`).then((res) => res.json());
		const jwksUri = oidcConfiguration?.jwks_uri;
		const jwks = await createRemoteJWKSet(new URL(jwksUri));
		const verificationResult = await jwtVerify(jwtToken, jwks, { issuer });
		console.log("JWT verification result:", verificationResult);
	} catch (error) {
		console.error("Failed to verify JWT:", error);
		throw new Error("Failed to verify the token");
	}
}

/**
 * Tracks whether the preflight phase (clears any previous user's Genesys session
 * before the real chat init) has completed for this chat window. Set to true
 * after WIPE_COMPLETE arrives from the message-widget iframe. Used to:
 *  - Switch the iframe src from `/message-widget.html?wipe=true` (preflight phase)
 *    to `/message-widget.html` (real phase) once the preflight has finished.
 *  - Skip the preflight on the bot-timeout iframe reload, so the user is never
 *    forced to authenticate twice within a single chat-window-open.
 */
let wipeDoneThisWindow = false;

const millisecondsPerMinute = 60 * 1000;

function checkDoesSessionIdExist() {
	const existingId = localStorage.getItem("chatSessionId");
	return Boolean(existingId);
}

const cannotOpenInMultipleWindowsMessage = `
	The chat cannot be opened in multiple windows or tabs at the same time.
	Please return to the tab or window where the chat is already open to continue your conversation.`;

/**
 * Attaches a persistent storage listener that keeps this window's disconnect UI in sync
 * with changes to chatSessionId made by other windows:
 * - session cleared → "other window closed, reload to open chat"
 * - session reclaimed by another window → "chat already open elsewhere"
 */
function attachDisconnectedSessionListener() {
	window.addEventListener("storage", function onChatSessionChanged(event) {
		if (event.key !== "chatSessionId") return;

		if (event.newValue === null) {
			overrideMessageContent(
				`The chat in the other window has been closed.
				Reload this page to open the chat.`,
				createReloadButton("Reload to open chat"),
				createCloseWindowButton(),
			);
		} else {
			overrideMessageContent(
				cannotOpenInMultipleWindowsMessage,
				createCloseWindowButton(),
				createUseThisWindowButton(),
			);
		}
	});
}

function disconnectIfChatAlreadyOpen() {
	if (checkDoesSessionIdExist()) {
		disconnectWithReason({
			reason: cannotOpenInMultipleWindowsMessage,
			buttons: [createCloseWindowButton(), createUseThisWindowButton()],
			disconnectServerSession: false, // keep the Genesys session alive so the user can continue the chat when they return to the original tab
		});
		attachDisconnectedSessionListener();
	} else {
		const newId = crypto.randomUUID();
		ownershipSessionId = newId;
		localStorage.setItem("chatSessionId", newId);

		// If another window removes or replaces the session ID (takeover), disconnect this window
		window.addEventListener("storage", function onSessionTakenOver(event) {
			if (event.key === "chatSessionId" && event.newValue !== newId) {
				window.removeEventListener("storage", onSessionTakenOver);
				// Release ownership so this window's later unload cannot destroy the new owner's key.
				ownershipSessionId = null;
				disconnectWithReason({
					reason: `
						The chat has been opened in another window.
						This window has been disconnected.`,
					buttons: [createCloseWindowButton(), createUseThisWindowButton()],
				});
				attachDisconnectedSessionListener();
			}
		});
	}
}

function clearSessionId() {
	if (ownershipSessionId === null) return;
	// Only remove the key if this tab still owns the session — another tab may have taken over.
	const isSessionOwnedByCurrentTab = localStorage.getItem("chatSessionId") === ownershipSessionId;
	if (isSessionOwnedByCurrentTab) {
		localStorage.removeItem("chatSessionId");
	}
	ownershipSessionId = null;
}

disconnectIfChatAlreadyOpen();

const EXPECTED_PARENT_ORIGINS: Array<string> = rawExpectedParentOrigins;

function checkIsExpectedOrigin(origin: string) {
	return EXPECTED_PARENT_ORIGINS.includes(origin);
}

let notificationCount = 0;
let isConversationEnded = false;

function incrementNotificationCount() {
	if (isConversationEnded) return;
	notificationCount++;
	document.title = document.title.replace(/^(\(\d+\) )?/, `(${notificationCount}) `);
}
function resetNotificationCount() {
	notificationCount = 0;
	document.title = document.title.replace(/^(\(\d+\) )/, "");
}

let questionnaireInactivityTimeoutId: ReturnType<typeof setTimeout> | null = null;
let hasReceivedHumanAgentMessage = false;

function stopQuestionnaireInactivityTimeout() {
	if (questionnaireInactivityTimeoutId) {
		clearTimeout(questionnaireInactivityTimeoutId);
		questionnaireInactivityTimeoutId = null;
	}
}

function createQuestionnaireInactivityTimeout() {
	if (hasDisconnected || hasReceivedHumanAgentMessage) return;
	stopQuestionnaireInactivityTimeout();
	questionnaireInactivityTimeoutId = setTimeout(() => {
		questionnaireInactivityTimeoutId = null;
		if (hasDisconnected || hasReceivedHumanAgentMessage) return;
		// Need to use the default message because it is currently impossible to differentiate the
		// start of chat questionnaire from some chat service paths like Certificate of Currency (CoC)
		disconnectWithReason();
	}, QUESTIONNAIRE_INACTIVITY_TIMEOUT_MINUTES * millisecondsPerMinute);
}

function resetQuestionnaireInactivityTimeout() {
	stopQuestionnaireInactivityTimeout();
	createQuestionnaireInactivityTimeout();
}

function clearConversationTimeoutState() {
	stopQuestionnaireInactivityTimeout();
	hasReceivedHumanAgentMessage = false;
}

function loadMessageIframe() {
	const environment = getEnvironment();

	// Preflight phase runs first on every fresh chat-window-open and clears any
	// previous user's Genesys session before the real init runs. After WIPE_COMPLETE
	// we set wipeDoneThisWindow and reload into the real phase. The bot-timeout
	// iframe reload (`reloadMessageWidgetIframe`) re-enters this function and
	// naturally skips the preflight because the flag is already true.
	const wipeMode = !wipeDoneThisWindow;
	const iframeUrl = wipeMode ? `/message-widget.html?wipe=true` : `/message-widget.html`;
	messageIframe.hidden = false;

	resumeLoadingTimeout();

	// Assign onload before navigating so the DATA-postMessage handler is registered before the load completes.
	messageIframe.onload = () => {
		// Don't need to pass auth results via postmessage here because they are sent via resolving the openOidcPopup() promise in utils.ts,
		// but we still want to trigger the onload when the iframe is ready so we can start the keep-alive and inactivity timers and end the loading state.
		messageIframe.contentWindow?.postMessage(
			{
				type: "DATA",
				isAuthenticated: isAuthenticatedSession,
				environment,
				jwtClaims: jwtResult.isValid ? (jwtResult.data ?? undefined) : undefined,
			} satisfies IframeDataMessage,
			messageIframe.contentWindow.origin,
		);
	};

	// location.replace rather than setting .src so this navigation does not add a
	// browser-history entry. A back button press would otherwise
	// unwind it and empty the chat instead of leaving the popup untouched.
	const iframeWindow = messageIframe.contentWindow;
	if (iframeWindow) {
		iframeWindow.location.replace(iframeUrl);
	} else {
		// defensive: contentWindow should never be null for an in-DOM iframe
		messageIframe.src = iframeUrl;
	}
}

function startBotTimeout() {
	botTimeoutId = setTimeout(reloadMessageWidgetIframe, BOT_TIMEOUT_MS);
}

function stopBotTimeout() {
	if (botTimeoutId) {
		clearTimeout(botTimeoutId);
		botTimeoutId = null;
	}
}

function stopConversationEndedTimeout() {
	if (conversationEndedTimeoutId) {
		clearTimeout(conversationEndedTimeoutId);
		conversationEndedTimeoutId = null;
	}
}

function reloadMessageWidgetIframe() {
	stopBotTimeout();
	if (botTimeoutReloadCount >= MAX_BOT_TIMEOUT_RELOADS) {
		console.warn(
			`Bot timeout: ${MAX_BOT_TIMEOUT_RELOADS} consecutive reloads with no chat activity — giving up and showing the load-error UI`,
		);
		showLoadError("bot-timeout-exhausted");
		return;
	}
	botTimeoutReloadCount += 1;
	console.log(
		`Bot timeout: no message received within ${botTimeoutSeconds} seconds — reloading message-widget iframe (attempt ${botTimeoutReloadCount}/${MAX_BOT_TIMEOUT_RELOADS})`,
	);
	resetAndReloadIframe();
}

/**
 * Reloads the message-widget iframe from scratch: clears any in-flight loading state, shows the
 * spinner, and points the iframe at about:blank before re-entering loadMessageIframe (the blank
 * step forces a reload even when the target URL is unchanged). Used by the bot-timeout reload path
 * (reloadMessageWidgetIframe).
 */
function resetAndReloadIframe() {
	botTimerWasPaused = false;
	stopLoadingTimeout();
	isLoadingComplete = false;
	showSpinner();
	// Hide any leftover popup-blocked / "Logging in, please wait..." panel from the
	// previous attempt so the user only sees a fresh spinner during the retry.
	disconnectReasonElem.hidden = true;
	// location.replace rather than setting .src so the reset navigation doesn't add a
	// browser-history entry (see loadMessageIframe). location.replace to the same URL
	// still forces the fresh reload the about:blank step exists to guarantee.
	messageIframe.contentWindow?.location.replace("about:blank");
	setTimeout(loadMessageIframe, 100);
}

/**
 * Handles a user-initiated trash-icon clear (the CLOSE_CHAT_WINDOW message) by closing the whole
 * chat window. Stops the bot and conversation-ended timers first — a trash clear's
 * conversationDisconnected arms the 30s CONVERSATION_ENDED timer before this runs, and the bot-timer
 * callback (reloadMessageWidgetIframe) still has no hasDisconnected guard — then closes the window.
 * window.close() only works when the page was opened by a script and window.closed does not flip to
 * true synchronously, so the end-of-chat fallback panel is deferred and only shown if the window is
 * genuinely still open a moment later.
 */
function closeChatWindowAfterUserClear() {
	// If the wrapper has already disconnected, its disconnect panel is showing and a late
	// conversationCleared must not act on it. A guest disconnect posts DISCONNECT to the iframe →
	// hardCloseMessenger → clearConversation → conversationCleared, which bounces CLOSE_CHAT_WINDOW
	// back here; without this guard that would slam the whole window shut over the panel.
	if (hasDisconnected) return;
	stopBotTimeout();
	stopConversationEndedTimeout();
	window.close();
	// Defer the fallback panel so it only shows if the window is genuinely still open a moment later —
	// a real close tears the page down before this timer fires. Skip too if some other path
	// disconnected the wrapper during the delay, so we don't overwrite an existing disconnect panel.
	setTimeout(() => {
		if (window.closed || hasDisconnected) return;
		// A CONVERSATION_ENDED that arrived after this fallback was scheduled (reverse event order on a
		// directly-opened tab) can arm a fresh 30s conversation-ended timer that the up-front stop above
		// didn't catch — cancel it here so it can't later overwrite the panel we're about to show.
		stopConversationEndedTimeout();
		disconnectWithReason();
	}, WINDOW_CLOSE_FALLBACK_DELAY_MS);
}

/** Stops the loading timeout to give people time to enter their auth details */
function stopLoadingTimeout() {
	if (loadingTimeoutId) {
		clearTimeout(loadingTimeoutId);
		loadingTimeoutId = null;
	}
}

/** Resumes the loading timeout to show an error if the chat fails to load */
function resumeLoadingTimeout() {
	if (!loadingTimeoutId) {
		loadingTimeoutId = setTimeout(() => showLoadError("loading timed out"), LOADING_TIMEOUT_MS);
	}
}

function hideSpinner() {
	const loadingElem = document.querySelector<HTMLElement>(".loading-spinner-container");
	// Use style.display rather than the `hidden` attribute: the CSS rule `display: grid` on
	// this element has equal specificity to the browser's `[hidden] { display: none }` rule
	// and wins the cascade tie, so the `hidden` attribute has no visual effect.
	if (loadingElem) loadingElem.style.display = "none";
}

function showSpinner() {
	const loadingElem = document.querySelector<HTMLElement>(".loading-spinner-container");
	if (loadingElem) loadingElem.style.display = ""; // remove inline override, CSS display:grid takes effect
}

function endLoadingIndicator() {
	isLoadingComplete = true;
	stopLoadingTimeout();
	hideSpinner();
	// In case the disconnect UI was shown for the popup-blocked retry button, hide it now
	disconnectReasonElem.hidden = true;
}

function showLoadError(errorCode: string = "unknown") {
	if (isLoadingComplete || hasDisconnected) return; // chat already loaded successfully or already disconnected
	isLoadingComplete = true;
	console.error(`Error loading the chat: ${errorCode}`);
	disconnectWithReason({ reason: CHAT_UNAVAILABLE_MESSAGE });
}

interface BaseDisconnectParams {
	buttons?: Array<HTMLButtonElement>;
	/** Whether to tell the server to end the Genesys session (`true`), or just close the chat on the client side without affecting the server session (`false`).
	 * Setting this to false allows the user to return to the original tab and continue the chat if they accidentally opened the chat in multiple tabs or if they closed the chat window but not the original tab.
	 * @default true */
	disconnectServerSession?: boolean;
}

interface DisconnectFromChatWithReasonParams extends BaseDisconnectParams {
	reason?: string;
}

function disconnectWithReason({
	reason = END_OF_SESSION_MESSAGE,
	buttons = [closeButton],
	...restParams
}: DisconnectFromChatWithReasonParams = {}) {
	disconnectFromChat({
		...restParams,
		buttons,
		data: {
			action: "DISCONNECT",
			reason,
		},
	});
}

interface DisconnectFromChatParams extends BaseDisconnectParams {
	data: PostMessageData;
}

function disconnectFromChat({ data, buttons = [], disconnectServerSession = true }: DisconnectFromChatParams) {
	clearSessionId();
	const reason = data.reason ?? "The chat has been disconnected.";
	stopLoadingTimeout();
	hideSpinner();

	if (disconnectServerSession) {
		clearConversationTimeoutState();
		// Tell the iframe to hard-close any partial Genesys session before removing it,
		// otherwise the message goes to a detached browsing context and is silently dropped.
		messageWindow?.postMessage(
			{
				action: "DISCONNECT",
				reason: reason,
			} satisfies PostMessageData,
			window.origin,
		);
	}

	// Do not remove messageIframe until after posting the DISCONNECT message,
	// otherwise the Genesys session might not be closed down properly.
	messageIframe?.remove();
	disconnectReasonElem.hidden = false;
	overrideMessageContent(reason, ...buttons);
	hasDisconnected = true;
}

function overrideMessageContent(newContent: string, ...buttons: Array<HTMLButtonElement>) {
	const messageContentElem = disconnectReasonElem.querySelector(".message-content")!;
	const messageActionsElem = disconnectReasonElem.querySelector(".message-actions")!;
	messageActionsElem.innerHTML = "";
	messageContentElem.innerHTML = "";
	messageContentElem.append(...convertStringToParagraphs(newContent));
	messageActionsElem.append(...buttons);
}

function createReloadButton(textContent: string) {
	const button = document.createElement("button");
	button.textContent = textContent;
	button.type = "button";
	button.onclick = () => {
		window.location.reload();
	};
	return button;
}

function createCloseWindowButton(textContent = "Close window") {
	const button = document.createElement("button");
	button.textContent = textContent;
	button.type = "button";
	button.onclick = () => {
		window.close();
	};
	return button;
}

function createUseThisWindowButton() {
	const button = document.createElement("button");
	button.textContent = "Chat in this window. All previous chat history will be lost";
	button.type = "button";
	button.onclick = () => {
		localStorage.removeItem("chatSessionId"); // triggers storage event in the active window, causing it to disconnect
		window.location.reload();
	};
	return button;
}

/**
 * Shows a "Log in" button in the disconnect reason panel so the user can manually
 * trigger the auth popup (a real click gesture bypasses the browser's popup blocker).
 * The widget-wrapper opens the popup itself, receives the AUTH_RESULT, and forwards
 * the result to the message-widget iframe as AUTH_RESULT.
 * The iframe is kept alive throughout so the Genesys auth flow can continue.
 */
function showPopupBlockedButton(authUrl: string) {
	const loginButton = document.createElement("button");
	loginButton.textContent = "Log in";
	loginButton.classList.add("manual-auth-button");

	overrideMessageContent(`The auto log in was blocked by your browser pop-up blocker.`, loginButton);

	loginButton.addEventListener("click", () => {
		loginButton.disabled = true;

		authPopupWindow = window.open(authUrl, "auth0-login", "width=500,height=700,scrollbars=yes");

		if (!authPopupWindow) {
			const reloadButton = createReloadButton("Reload the chat");
			// Still blocked even on a direct click — show a plain text error
			overrideMessageContent(
				`Pop-ups are blocked by your browser.
				Please enable pop-ups for this site, then reload the chat.`,
				reloadButton,
			);
			messageWindow?.postMessage({ action: "AUTH_CANCELLED" } satisfies PostMessageData, window.origin);
			return;
		}

		// Popup opened — update UI to show a waiting state.
		// Don't start the loading timeout here — the user may take any amount of time to log in.
		// It will be restarted by RESUME_TIMEOUT once getAuthCode resolves successfully.
		overrideMessageContent("Logging in, please wait...");

		// Forward the auth callback from the popup to the message-widget iframe
		function handleAuthCallback(event: MessageEvent<PostMessageData>) {
			if (event.origin !== window.location.origin) return;
			if (event.data?.action !== "AUTH_RESULT") return;
			window.removeEventListener("message", handleAuthCallback);
			clearInterval(pollTimer);
			messageWindow?.postMessage(
				{
					...event.data,
					action: "AUTH_RESULT",
				} satisfies PostMessageData,
				window.origin,
			);
		}
		window.addEventListener("message", handleAuthCallback);

		// Detect if the user closes the popup without completing login
		const pollTimer = setInterval(() => {
			if (!authPopupWindow || authPopupWindow.closed) {
				clearInterval(pollTimer);
				window.removeEventListener("message", handleAuthCallback);
				authPopupWindow = null;
				messageWindow?.postMessage({ action: "AUTH_CANCELLED" } satisfies PostMessageData, window.origin);
			}
		}, 500);
	});
}

function appendTextWithLinks(element: HTMLElement, text: string) {
	let lastIndex = 0;

	// Find URLs in the text and split them into anchor elements.
	for (const match of text.matchAll(URL_REGEX)) {
		const matchStart = match.index!;
		const matchEnd = matchStart + match[0].length;

		// Append any plain text before this URL (safe via textContent).
		if (matchStart > lastIndex) {
			element.appendChild(document.createTextNode(text.slice(lastIndex, matchStart)));
		}

		// Validate the URL before creating the anchor.
		try {
			const parsed = new URL(match[0]);
			// Only allow http: and https: schemes to block javascript: etc.
			if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
				element.appendChild(document.createTextNode(match[0]));
			} else {
				const anchor = document.createElement("a");
				anchor.setAttribute("href", parsed.href);
				anchor.textContent = match[0]; // textContent is XSS-safe
				element.appendChild(anchor);
			}
		} catch {
			// Malformed URL — render as plain text.
			element.appendChild(document.createTextNode(match[0]));
		}

		lastIndex = matchEnd;
	}

	// Append any remaining plain text after the last URL.
	if (lastIndex < text.length) {
		element.appendChild(document.createTextNode(text.slice(lastIndex)));
	}
}

function convertStringToParagraphs(str: string): HTMLElement[] {
	const elements: HTMLElement[] = [];
	let list: HTMLUListElement | null = null;

	for (const line of str.trim().split("\n")) {
		const trimmed = line.trim();
		const listItemMatch = /^-\s*(.+)$/.exec(trimmed);

		if (listItemMatch) {
			if (!list) {
				list = document.createElement("ul");
				elements.push(list);
			}
			const listItem = document.createElement("li");
			appendTextWithLinks(listItem, listItemMatch[1]);
			list.appendChild(listItem);
			continue;
		}

		list = null;
		const paragraph = document.createElement("p");
		appendTextWithLinks(paragraph, trimmed);
		elements.push(paragraph);
	}

	return elements;
}

async function initialiseChatWidget() {
	window.addEventListener("message", (messageEvent: MessageEvent<PostMessageData>) => {
		console.log("Received message event:", messageEvent);
		const isChildIframeMessage = messageEvent.source === messageIframe.contentWindow;
		const isAuthPopupMessage = authPopupWindow !== null && messageEvent.source === authPopupWindow;
		const isFromExpectedOrigin = checkIsExpectedOrigin(messageEvent.origin);

		const isKnownSource = isFromExpectedOrigin && (isChildIframeMessage || isAuthPopupMessage);

		if (!isKnownSource) {
			/*
				Ignore messages from unknown sources.
				Messages could come from stray Genesys messages reaching the top window, or browser extensions triggering postmessage events.
				Neither scenario warrant a full disconnect. Returning early to ignore the message is a safe appropriate middle-ground.
			*/
			console.warn("Received communication from an unexpected source:", messageEvent.source, messageEvent.origin);
			return;
		}

		const messageData: PostMessageData = messageEvent.data ?? {};

		const { action, hasReceivedBotTimeoutSignalMessage } = messageData;

		console.log("widget-wrapper received message event:", messageData);

		switch (action) {
			case "WIPE_COMPLETE":
				if (wipeDoneThisWindow) break; // ignore duplicates
				// If the wrapper has already disconnected (e.g. preflight hit
				// LOADING_TIMEOUT_MS and showLoadError tore the iframe down), a late
				// WIPE_COMPLETE from the old iframe must not start the real phase —
				// doing so would resurrect a detached Genesys session after the user
				// was already shown a disconnect.
				//
				// Note: the two console messages below say "preflight" rather than
				// naming what's really happening (clearing the previous user's session
				// to stop it leaking into the new one). See the long comment in
				// runWipePhaseAuth (src/message-widget.ts) for why — short version:
				// don't broadcast in DevTools that another user's auth tokens were
				// sitting in browser storage. Do NOT "improve" these strings.
				if (hasDisconnected) {
					console.warn("Ignoring late preflight signal — already disconnected");
					break;
				}
				wipeDoneThisWindow = true;
				console.log("Preflight complete — loading chat");
				// Reset the loading timeout so the real phase gets a full window. The
				// existing showLoadError path covers any failure during either phase, so
				// we don't need a separate preflight-only timeout — if preflight hangs,
				// LOADING_TIMEOUT_MS triggers showLoadError and we never advance.
				stopLoadingTimeout();
				loadMessageIframe();
				break;
			case "MESSENGER_READY":
				console.log(`Messenger ready — starting ${botTimeoutSeconds}-second bot timeout`);
				startBotTimeout();
				break;
			case "MESSENGER_END_LOAD":
				endLoadingIndicator();
				createQuestionnaireInactivityTimeout();
				break;
			case "LOAD_ERROR":
				// Iframe-side senders use sendActionToParent(action, reason), so the error
				// code arrives in `reason`. The `error` field is only populated on
				// AUTH_RESULT-style messages. Prefer `error` when present for back-compat,
				// otherwise fall back to `reason`, then to "unknown" if neither is set.
				showLoadError(messageData.error ?? messageData.reason ?? "unknown loading issue");
				break;
			case "PLUGIN_LOAD_FAILURE":
				// The iframe detected a Genesys SDK plugin script-load failure (a known
				// transient cold-cache failure mode for messagingservice.min.js /
				// launcher.min.js fetched from apps.mypurecloud.com.au — see the
				// listeners in message-widget.ts). Delegate to reloadMessageWidgetIframe
				// so the failure consumes from the same MAX_BOT_TIMEOUT_RELOADS budget
				// as bot-timeouts; both signals mean "iframe is broken, try again".
				// loadMessageIframe will re-enter the same phase (preflight or real)
				// based on wipeDoneThisWindow, so coverage is symmetric across phases.
				//
				// Drop late messages from a torn-down iframe: if the wrapper has already
				// disconnected (e.g. LOADING_TIMEOUT_MS fired showLoadError, which removed
				// the iframe), reloading would resurrect the iframe after the user has
				// already been shown the disconnect UI. Same guard as WIPE_COMPLETE above.
				if (hasDisconnected) {
					console.warn("Ignoring PLUGIN_LOAD_FAILURE — already disconnected");
					break;
				}
				console.warn(
					`PLUGIN_LOAD_FAILURE from message-widget — auto-reloading iframe (${messageData.reason ?? "unknown reason"})`,
				);
				reloadMessageWidgetIframe();
				break;
			case "POPUP_BLOCKED":
				stopLoadingTimeout();
				hideSpinner();
				disconnectReasonElem.hidden = false;
				showPopupBlockedButton(messageData.authUrl ?? "");
				console.error("Authentication popup was blocked by the browser");
				break;
			case "AUTH_CANCELLED": {
				const reloadButton = createReloadButton("Try again");
				disconnectWithReason({
					reason: `Authentication was cancelled.`,
					buttons: [closeButton, reloadButton],
				});
				break;
			}
			case "AUTH_RESULT":
				stopLoadingTimeout();
				if (messageData.error) {
					showLoadError(messageData.error ?? "unknown authentication issue");
				}
				break;
			case "PAUSE_TIMEOUT":
				if (botTimeoutId) {
					stopBotTimeout();
					botTimerWasPaused = true;
				}
				stopLoadingTimeout();
				break;
			case "RESUME_TIMEOUT":
				if (botTimerWasPaused) {
					botTimerWasPaused = false;
					startBotTimeout();
				}
				resumeLoadingTimeout();
				break;
			case "NOTIFY_INCREMENT":
				incrementNotificationCount();
				break;
			case "MESSAGE_ACTIVITY":
				stopBotTimeout();
				// We want to reset the timeout anytime any message is sent, not just the customer sent messages
				// This ensures that when waiting for an agent to respond, the customer is not kicked out of the chat
				resetQuestionnaireInactivityTimeout();
				botTimerWasPaused = false;
				// Real chat activity proves the session is alive — clear the bot-timeout
				// reload budget so any future stall starts fresh instead of inheriting
				// the count from earlier-in-the-session retries.
				botTimeoutReloadCount = 0;
				break;
			case "AGENT_MESSAGE":
				// AGENT_MESSAGE is exclusively Human-originated content. Its first
				// occurrence permanently transitions the current conversation out of
				// the questionnaire inactivity policy.
				if (!hasReceivedHumanAgentMessage) {
					hasReceivedHumanAgentMessage = true;
					stopQuestionnaireInactivityTimeout();
				}
				break;
			case "BOT_MESSAGE":
				// Reserved for future wrapper-side bot-message behaviour.
				break;
			case "CUSTOMER_MESSAGE":
				// The SDK has confirmed a user-sent message. It clears outstanding
				// notifications and permits future agent messages to be counted if
				// this starts or resumes a conversation after a server-side end.
				isConversationEnded = false;
				resetNotificationCount();
				createQuestionnaireInactivityTimeout();
				stopConversationEndedTimeout();
				break;
			case "CLEAR_SURVEY_URL":
				clearConversationTimeoutState();
				break;
			case "CLOSE_CHAT_WINDOW":
				// The user cleared the chat via the trash icon — close the whole chat window.
				closeChatWindowAfterUserClear();
				break;
			case "CONVERSATION_ENDED": {
				// The SDK can deliver late agent-message notifications after this event.
				// Keep the conversation in its ended state so those increments cannot
				// restore a title count after it has been cleared.
				isConversationEnded = true;
				resetNotificationCount();
				if (hasDisconnected || conversationEndedTimeoutId || !isLoadingComplete) break;
				clearConversationTimeoutState();

				if (hasReceivedBotTimeoutSignalMessage) {
					endChat(USER_UNRESPONSIVE_MESSAGE);
				} else {
					conversationEndedTimeoutId = setTimeout(() => {
						endChat(END_OF_SESSION_MESSAGE);
					}, CONVERSATION_END_TIMEOUT_MINUTES * millisecondsPerMinute);
				}

				function endChat(reason: string) {
					stopConversationEndedTimeout();
					// Never overwrite an already-shown disconnect panel — e.g. a trash-icon window-close
					// fallback that ran first (reverse event order) already rendered its own panel.
					if (hasDisconnected) return;
					disconnectWithReason({ reason });
				}

				break;
			}
			default:
				console.warn("Unknown message action received:", { messageEvent, action });
		}
	});

	window.addEventListener("beforeunload", () => {
		clearSessionId();
	});
	window.addEventListener("pagehide", () => {
		clearSessionId();
	});

	// It is preferable for this to not be brand specific since we need to support multiple brands and this message is shared between them
	const pleaseOpenFromOfficialSiteMessage =
		"\nPlease ensure that you are opening the chat from an official website or app.";

	if (!checkIsExpectedOrigin(location.origin)) {
		showLoadError(
			`The chat cannot be loaded on this domain: ${location.origin}.${pleaseOpenFromOfficialSiteMessage}`,
		);
	} else {
		// Bail if a disconnect already happened before we got here — e.g. the chat is open
		// in another tab, so disconnectIfChatAlreadyOpen() already showed the multiple-windows
		// panel (with its takeover button). Overwriting it with the unavailable panel would lose
		// that option. Mirrors the hasDisconnected guard used by the other late-arriving paths
		// (WIPE_COMPLETE, PLUGIN_LOAD_FAILURE, showLoadError). Also skips a pointless network call.
		if (hasDisconnected) return;

		// Detect an inactive/unavailable Genesys deployment before loading the iframe.
		// When the deployment is disabled the SDK never boots and the chat would otherwise
		// hang on the spinner until LOADING_TIMEOUT_MS — check the deployment config up front
		// and disconnect immediately instead.
		try {
			const deploymentId = getDeploymentId({ isAuthenticated: isAuthenticatedSession });
			const isDeploymentActive = await checkIsDeploymentActive(deploymentId);

			// Re-check after the await: a disconnect may have occurred while the config fetch was in
			// flight. Do not overwrite that panel or load the iframe into an already-torn-down wrapper.
			if (hasDisconnected) return;

			if (!isDeploymentActive) {
				showLoadError("Deployment is inactive");
				return;
			}
			loadMessageIframe();
		} catch (error) {
			const errorReason = "Failed to check the Genesys deployment status";
			console.error(`${errorReason}:`, error);
			showLoadError(errorReason);
		}
	}
}
